import axios from "axios";
import ipponApi from "@/config/axios";
import { resolveAiBlockedMessage } from "@/constants/clientAccess";

export type ReversePostalCodeLookupParams = {
  prefecture: string | number;
  city: string;
  blockNumber?: string;
};

export type ReversePostalCodeLookupResult = {
  postalCode: string;
  isAmbiguous?: boolean;
  candidateCount?: number;
};

export class ReversePostalCodeLookupError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "ReversePostalCodeLookupError";
    this.status = status;
  }
}

const normalizeLookupText = (value: string) =>
  value
    .normalize("NFKC")
    .trim()
    .replace(/[‐‑‒–—―ー－]/g, "-")
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, " ");

const PREFECTURE_NAME_TO_CODE: Record<string, string> = {
  北海道: "01", 青森県: "02", 岩手県: "03", 宮城県: "04", 秋田県: "05",
  山形県: "06", 福島県: "07", 茨城県: "08", 栃木県: "09", 群馬県: "10",
  埼玉県: "11", 千葉県: "12", 東京都: "13", 神奈川県: "14", 新潟県: "15",
  富山県: "16", 石川県: "17", 福井県: "18", 山梨県: "19", 長野県: "20",
  岐阜県: "21", 静岡県: "22", 愛知県: "23", 三重県: "24", 滋賀県: "25",
  京都府: "26", 大阪府: "27", 兵庫県: "28", 奈良県: "29", 和歌山県: "30",
  鳥取県: "31", 島根県: "32", 岡山県: "33", 広島県: "34", 山口県: "35",
  徳島県: "36", 香川県: "37", 愛媛県: "38", 高知県: "39", 福岡県: "40",
  佐賀県: "41", 長崎県: "42", 熊本県: "43", 大分県: "44", 宮崎県: "45",
  鹿児島県: "46", 沖縄県: "47",
};

const normalizePrefectureCode = (value: string | number) => {
  const str = String(value ?? "").trim();
  // 都道府県名の場合はコードに変換
  if (PREFECTURE_NAME_TO_CODE[str]) return PREFECTURE_NAME_TO_CODE[str];
  // 数値コードの場合はゼロ埋め
  return str.replace(/\D/g, "").padStart(2, "0");
};

const DEFAULT_ERROR_MESSAGE = "郵便番号検索に失敗しました";

// サーバー/axios のメッセージは英語や XML (S3 の MethodNotAllowed 等) が
// 混ざるため画面には出さず、ステータスから日本語の固定文言を返す。
export const getReverseLookupErrorMessage = (status?: number): string => {
  if (status === undefined) {
    return "郵便番号検索サーバーに接続できませんでした。時間をおいて再度お試しください";
  }
  switch (status) {
    case 400:
    case 422:
      return "入力された住所を確認してください";
    case 401:
    case 403:
      return "認証に失敗しました。再度ログインしてください";
    case 404:
      return "該当する郵便番号が見つかりません";
    default:
      return DEFAULT_ERROR_MESSAGE;
  }
};

export const reverseLookupPostalCode = async ({
  prefecture,
  city,
  blockNumber,
}: ReversePostalCodeLookupParams): Promise<ReversePostalCodeLookupResult> => {
  try {
    // 物件管理 BE の中継 API。物件管理 BE が AI 停止の判定と利用計測（IPPON AIエージェント）をして、ippon-ai-common へ中継する
    const response = await ipponApi.post(
      "/api/v1/postal-codes/reverse-search",
      {
        prefecture: normalizePrefectureCode(prefecture),
        city: normalizeLookupText(city),
        block_number: normalizeLookupText(blockNumber || ""),
      }
    );
    const data = response.data?.data ?? {};

    return {
      postalCode: String(data.postal_code ?? "").replace(/[^0-9]/g, ""),
      isAmbiguous: Boolean(data.is_ambiguous),
      candidateCount:
        typeof data.candidate_count === "number"
          ? data.candidate_count
          : undefined,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      // IPPON AIエージェントを止めた 403（トライアルの AI 無料枠・解約後の猶予期間）は、理由に手入力の案内を添える
      const aiBlockedMessage = resolveAiBlockedMessage(error.response?.data);
      if (aiBlockedMessage) {
        throw new ReversePostalCodeLookupError(`${aiBlockedMessage}郵便番号は直接入力してください。`, status);
      }
      throw new ReversePostalCodeLookupError(
        getReverseLookupErrorMessage(status),
        status
      );
    }

    throw new ReversePostalCodeLookupError(DEFAULT_ERROR_MESSAGE);
  }
};
